import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

// Types for response data
export interface ClientIncident {
  id?: string;
  date: string;
  time: string;
  location: string;
  description: string;
  individuals: string;
  witnesses: string;
  witness_contact: string;
  actions_during: string;
  client_name: string;
  ndis_number: string;
  client_condition: string;
  injured: string;
  injury_details: string;
  first_aid: string;
  first_aid_details: string;
  property_damage: string;
  damage_description: string;
  actions_after: string;
  escalated: string;
  manager_name: string;
  reporting_time: string;
  externally_reported: string;
  external_party: string;
  follow_up: string;
  recommendations: string;
  attachments: string;
  sw_name: string;
  sw_role: string;
  sw_signature: string;
  date_completed: string;
  created_by: string;
  created_at: string;
  photo?: string;
  resolved?: string;
}

export interface DataBreach {
  id?: string;
  date: string;
  time: string;
  location: string;
  type_of_data: string;
  description: string;
  individuals_affected: string;
  contact_info: string;
  immediate_actions: string;
  breach_reported_externally: string;
  reported_to: string;
  prevention: string;
  reporter_name: string;
  signature: string;
  date_completed: string;
  created_by: string;
  created_at: string;
  photo?: string;
  resolved?: string;
}

export interface EmployeeIncident {
  id?: string;
  date: string;
  time: string;
  location: string;
  employee_name: string;
  job_title: string;
  description: string;
  other_individuals: string;
  witnesses: string;
  witness_contacts: string;
  immediate_actions: string;
  reported_to_management: string;
  manager_name: string;
  reporting_datetime: string;
  recommendations: string;
  employee_signature: string;
  date_completed: string;
  created_by: string;
  created_at: string;
  photo?: string;
  resolved?: string;
}

export interface MedicationIncident {
  id?: string;
  date: string;
  time: string;
  location: string;
  client_name: string;
  support_worker_name: string;
  medication_details: string;
  nature_of_incident: string;
  other_incident_details?: string;
  description: string;
  immediate_actions: string;
  reported_to_healthcare: string;
  healthcare_contact?: string;
  recommendations: string;
  support_worker_signature: string;
  date_completed: string;
  photo?: string;
  client_email?: string;
  resolved?: string;
}

export interface EquipmentIncident {
  id?: string;
  date: string;
  time: string;
  location: string;
  incident_type: string;
  description: string;
  damage_or_injury: string;
  was_injury: string;
  immediate_actions: string;
  maintenance_requested: string;
  reporter_name: string;
  signature: string;
  date_completed: string;
  photo?: string;
  client_email?: string;
  created_by: string;
  created_at: string;
  resolved?: string;
}

export interface RestrictivePractices {
  id?: string;
  date: string;
  time: string;
  location: string;
  client_name: string;
  support_worker_name: string;
  type_of_practice: string;
  reason_for_use: string;
  approved_in_plan: string;
  description: string;
  immediate_actions: string;
  reported_to_management: string;
  recommendations: string;
  support_worker_signature: string;
  date_completed: string;
  created_by: string;
  created_at: string;
  photo: string;
  resolved?: string;
}
// Type for API payload
export interface IncidentsPayload {
  group_id: string;
  service_id: string;
  client_email?: string;
}

// Type for API response
export interface IncidentsResponse {
  client_incident: ClientIncident[];
  data_breach: DataBreach[];
  employee_incident: EmployeeIncident[];
  medication_incident: MedicationIncident[];
  equipment_incident: EquipmentIncident[];
  restrictive_practices: RestrictivePractices[];
}

// State type
export interface IncidentsState {
  client_incident: ClientIncident[];
  data_breach: DataBreach[];
  employee_incident: EmployeeIncident[];
  medication_incident: MedicationIncident[];
  equipment_incident: EquipmentIncident[];
  restrictive_practices: RestrictivePractices[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: IncidentsState = {
  client_incident: [],
  data_breach: [],
  employee_incident: [],
  medication_incident: [],
  equipment_incident: [],
  restrictive_practices: [],
  loading: false,
  error: null
};

// Async thunk for fetching incidents
export const fetchIncidents = createAsyncThunk<
  IncidentsResponse,
  { client_email?: string },
  { rejectValue: string }
>('incidents/fetchIncidents', async ({ client_email }, { rejectWithValue }) => {
  try {
    // Retrieve data from localStorage
    const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as {
      selectedServiceTitle?: string;
    };
    const group_id = project.projectId || '';
    const service_id = service.selectedServiceTitle || '';

    if (!group_id || !service_id) {
      return rejectWithValue('Missing group_id or service_id in localStorage');
    }

    const payload: IncidentsPayload = {
      group_id,
      service_id,
      client_email
    };

    const response = await axiosInstance.post('/ndis/form/get-incidents', payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch incidents data');
  }
});

const incidentsListSlice = createSlice({
  name: 'incidents',
  initialState,
  reducers: {
    clearIncidentsState: (state) => {
      state.client_incident = [];
      state.data_breach = [];
      state.employee_incident = [];
      state.medication_incident = [];
      state.equipment_incident = [];
      state.restrictive_practices = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIncidents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIncidents.fulfilled, (state, action) => {
        state.loading = false;
        state.client_incident = action.payload.client_incident;
        state.data_breach = action.payload.data_breach;
        state.employee_incident = action.payload.employee_incident;
        state.medication_incident = action.payload.medication_incident;
        state.equipment_incident = action.payload.equipment_incident;
        state.restrictive_practices = action.payload.restrictive_practices;
      })
      .addCase(fetchIncidents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearIncidentsState } = incidentsListSlice.actions;
export default incidentsListSlice.reducer;
